import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { createVisitorPublic, leaveEventForCurrentUser } from '../api/visitors-service';

export interface Visitor {
    id?: string;
    eventId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    company?: string;
    jobTitle?: string;
    country?: string;
    city?: string;
    visitPurpose?: string;
    visitDate?: string;
    status?: string;
    visitedFairBefore?: boolean;
    isOrganizationMember?: boolean;
    marketingOptIn?: boolean;
}

interface VisitorsState {
    visitors: Visitor[];
    joinedVisitorEvents: string[];
    isLoading: boolean;
    error: string | null;
}

const initialState: VisitorsState = {
    visitors: [],
    joinedVisitorEvents: [],
    isLoading: false,
    error: null,
};

// Async thunks
export const createVisitor = createAsyncThunk(
    'visitors/createVisitor',
    async (payload: Visitor) => {
        const response = await createVisitorPublic(payload);
        return response.data;
    }
);

export const removeVisitor = createAsyncThunk(
    'visitors/removeVisitor',
    async ({ eventId }: { eventId: string; email?: string; phone?: string }) => {
        await leaveEventForCurrentUser(eventId);
        return eventId;
    }
);

const visitorsSlice = createSlice({
    name: 'visitors',
    initialState,
    reducers: {
        setJoinedVisitorEvents: (state, action: PayloadAction<string[]>) => {
            state.joinedVisitorEvents = action.payload;
        },
        addJoinedVisitorEvent: (state, action: PayloadAction<string>) => {
            if (!state.joinedVisitorEvents.includes(action.payload)) {
                state.joinedVisitorEvents.push(action.payload);
            }
        },
        removeJoinedVisitorEvent: (state, action: PayloadAction<string>) => {
            state.joinedVisitorEvents = state.joinedVisitorEvents.filter(
                (id) => id !== action.payload
            );
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Create visitor
        builder
            .addCase(createVisitor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createVisitor.fulfilled, (state, action) => {
                state.isLoading = false;
                // Add to joined events if successful
                if (action.meta.arg.eventId) {
                    state.joinedVisitorEvents.push(action.meta.arg.eventId);
                }
            })
            .addCase(createVisitor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to create visitor';
            });

        // Remove visitor
        builder
            .addCase(removeVisitor.fulfilled, (state, action) => {
                state.joinedVisitorEvents = state.joinedVisitorEvents.filter(
                    (id) => id !== action.payload
                );
            })
            .addCase(removeVisitor.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to remove visitor';
            });
    },
});

export const {
    setJoinedVisitorEvents,
    addJoinedVisitorEvent,
    removeJoinedVisitorEvent,
    clearError,
} = visitorsSlice.actions;
export default visitorsSlice.reducer;
